import { createContext, MouseEvent, ReactNode, useContext, useState } from 'react';
import { useParams } from 'react-router-dom';
import { UUID } from '@myme/utils';
import { useMount, useTranslation } from 'src/hooks';
import { useDialog } from 'src/components/organisms';
import { useBlobContext } from 'src/components/pages/mymes/editor/blobs/editor/context';

interface RemoveFileDialog {
  isOpen: boolean;
  closeDialog: () => void;
}

interface MediaContextValue {
  fileUUID: UUID | null;
  removeFileDialog: RemoveFileDialog;
  clearFileUUID: () => void;
  handleSelectFile: (e: React.MouseEvent<HTMLElement>, fileId: string) => void;
  handleClickAway: () => void;
  handleDeleteFile: () => void;
}

export const MediaContext = createContext<MediaContextValue | undefined>(undefined);

interface Props {
  children: ReactNode;
}

export const MediaContextProvider = ({ children }: Props) => {
  const {
    editor: { blob }
  } = useBlobContext();
  const [fileUUID, setFileUUID] = useState<UUID | null>(null);
  const removeFileDialog = useDialog();
  const { blobId } = useParams();

  useMount(() => {
    const isMediaBlobType = blob.type === 'VIDEO' || blob.type === 'IMAGE' || blob.type === 'AUDIO';
    if (blobId && isMediaBlobType && blob.mediaLibraryId) {
      setFileUUID(blob.mediaLibraryId);
    }
  });

  const handleSelectFile = (e: MouseEvent<HTMLElement>, fileId: string) => {
    e.stopPropagation();
    setFileUUID(fileId === fileUUID ? null : fileId);
  };

  const handleClickAway = () => setFileUUID(null);

  const handleDeleteFile = () => {
    removeFileDialog.openDialog();
  };

  const clearFileUUID = () => {
    setFileUUID(null);
  };

  return (
    <MediaContext.Provider
      value={{
        fileUUID,
        removeFileDialog,
        clearFileUUID,
        handleSelectFile,
        handleClickAway,
        handleDeleteFile
      }}
    >
      {children}
    </MediaContext.Provider>
  );
};

export function useMediaContext() {
  const { t } = useTranslation();
  const context = useContext(MediaContext);

  if (context === undefined) {
    throw new Error(t('errors.validation.context', { contextName: 'Media' }));
  }

  return context;
}
